/**
 * Module Schema Definitions
 * Defines required and optional fields for different module types
 */

export const MODULE_SCHEMAS = {
  events: {
    name: "Events",
    description: "Campus events and activities",
    required: [
      {
        key: "title",
        label: "Event Title",
        type: "string",
        description: "Name or title of the event"
      },
      {
        key: "description", 
        label: "Description",
        type: "string",
        description: "Event description or details"
      },
      {
        key: "time",
        label: "Date/Time",
        type: "datetime",
        description: "When the event takes place"
      },
      {
        key: "location",
        label: "Location",
        type: "string", 
        description: "Where the event takes place"
      }
    ],
    optional: [
      {
        key: "photoURL",
        label: "Photo URL",
        type: "url",
        description: "Image associated with the event"
      },
      {
        key: "organizer",
        label: "Organizer",
        type: "string",
        description: "Organization or person hosting the event"
      },
      {
        key: "siteURL",
        label: "Event URL",
        type: "url", 
        description: "Link to event details or registration"
      },
      {
        key: "endTime",
        label: "End Time",
        type: "datetime",
        description: "When the event ends"
      },
      {
        key: "category",
        label: "Category",
        type: "string",
        description: "Type or category of event"
      }
    ]
  },

  menus: {
    name: "Dining Menus",
    description: "Dining hall menus and food offerings", 
    required: [
      {
        key: "diningHall",
        label: "Dining Hall",
        type: "string",
        description: "Name of the dining location"
      },
      {
        key: "meal",
        label: "Meal Type",
        type: "string", 
        description: "Breakfast, lunch, dinner, etc."
      },
      {
        key: "date",
        label: "Date",
        type: "date",
        description: "Date the menu is for"
      },
      {
        key: "items",
        label: "Menu Items",
        type: "array",
        description: "List of food items available"
      }
    ],
    optional: [
      {
        key: "station",
        label: "Station/Counter",
        type: "string",
        description: "Specific serving area within dining hall"
      },
      {
        key: "allergens",
        label: "Allergen Info", 
        type: "array",
        description: "Allergen warnings for menu items"
      },
      {
        key: "nutritionInfo",
        label: "Nutrition",
        type: "object",
        description: "Nutritional information"
      },
      {
        key: "prices",
        label: "Prices",
        type: "object", 
        description: "Cost information if applicable"
      }
    ]
  },

  courses: {
    name: "Course Catalog",
    description: "Academic courses and class information",
    required: [
      {
        key: "courseCode",
        label: "Course Code",
        type: "string",
        description: "Course identifier (e.g., CSCI 101)"
      },
      {
        key: "title",
        label: "Course Title", 
        type: "string",
        description: "Full name of the course"
      },
      {
        key: "instructor",
        label: "Instructor",
        type: "string",
        description: "Professor or teacher name"
      },
      {
        key: "schedule",
        label: "Schedule",
        type: "string",
        description: "Meeting times and days"
      }
    ],
    optional: [
      {
        key: "description",
        label: "Description",
        type: "string", 
        description: "Course description or syllabus"
      },
      {
        key: "credits",
        label: "Credits",
        type: "number",
        description: "Credit hours"
      },
      {
        key: "prerequisites",
        label: "Prerequisites",
        type: "string",
        description: "Required prior courses"
      },
      {
        key: "location",
        label: "Location",
        type: "string",
        description: "Classroom or building"
      },
      {
        key: "semester",
        label: "Semester",
        type: "string", 
        description: "Academic term"
      },
      {
        key: "enrollment",
        label: "Enrollment Info",
        type: "object",
        description: "Capacity and availability"
      }
    ]
  },

  news: {
    name: "News & Announcements", 
    description: "Campus news and official announcements",
    required: [
      {
        key: "headline",
        label: "Headline",
        type: "string",
        description: "Article title or headline"
      },
      {
        key: "content",
        label: "Content",
        type: "string",
        description: "Article body or summary"
      },
      {
        key: "publishDate",
        label: "Publish Date",
        type: "date",
        description: "When the article was published"
      }
    ],
    optional: [
      {
        key: "author",
        label: "Author",
        type: "string",
        description: "Article author or byline"
      },
      {
        key: "imageURL",
        label: "Featured Image",
        type: "url", 
        description: "Main article image"
      },
      {
        key: "category",
        label: "Category",
        type: "string",
        description: "News category or section"
      },
      {
        key: "tags",
        label: "Tags",
        type: "array",
        description: "Article tags or keywords"
      },
      {
        key: "sourceURL",
        label: "Source URL",
        type: "url",
        description: "Link to full article"
      }
    ]
  },

  directory: {
    name: "Campus Directory",
    description: "Faculty, staff, and student directory information",
    required: [
      {
        key: "name",
        label: "Full Name", 
        type: "string",
        description: "Person's full name"
      },
      {
        key: "role",
        label: "Role/Title",
        type: "string",
        description: "Job title or academic position"
      },
      {
        key: "department",
        label: "Department",
        type: "string",
        description: "Academic or administrative department"
      }
    ],
    optional: [
      {
        key: "email",
        label: "Email",
        type: "email",
        description: "Contact email address"
      },
      {
        key: "phone",
        label: "Phone",
        type: "string",
        description: "Phone number"
      },
      {
        key: "office",
        label: "Office Location", 
        type: "string",
        description: "Office room or building"
      },
      {
        key: "photoURL",
        label: "Photo",
        type: "url",
        description: "Profile picture"
      },
      {
        key: "bio",
        label: "Biography",
        type: "string",
        description: "Professional biography"
      },
      {
        key: "specialties",
        label: "Specialties",
        type: "array",
        description: "Areas of expertise"
      }
    ]
  }
};

/**
 * Get schema for a module type
 * @param {string} moduleType - The type of module
 * @returns {Object|null} Schema definition or null if not found
 */
export const getModuleSchema = (moduleType) => {
  return MODULE_SCHEMAS[moduleType] || null;
};

/**
 * Get all available module types
 * @returns {Array} List of module type keys
 */
export const getAvailableModuleTypes = () => {
  return Object.keys(MODULE_SCHEMAS);
};

/**
 * Get all fields (required + optional) for a module type
 * @param {string} moduleType - The type of module
 * @returns {Array} Combined list of all fields
 */
export const getAllFields = (moduleType) => {
  const schema = getModuleSchema(moduleType);
  if (!schema) return [];
  
  return [
    ...schema.required.map(field => ({ ...field, isRequired: true })),
    ...schema.optional.map(field => ({ ...field, isRequired: false }))
  ];
};

/**
 * Validate if a field mapping is complete for a module type
 * @param {string} moduleType - The type of module
 * @param {Object} fieldMappings - Mapping of field keys to selectors
 * @returns {Object} Validation result with isValid and missing fields
 */
export const validateFieldMapping = (moduleType, fieldMappings) => {
  const schema = getModuleSchema(moduleType);
  if (!schema) {
    return { isValid: false, errors: ['Invalid module type'] };
  }

  const missing = schema.required.filter(field => 
    !fieldMappings[field.key] || !fieldMappings[field.key].trim()
  );

  return {
    isValid: missing.length === 0,
    missingFields: missing.map(field => field.label),
    errors: missing.length > 0 ? ['Missing required field mappings'] : []
  };
};